// Ingest allowlist for reference uploads (provider docs matrix: PNG/JPEG/WebP + HEIC→JPEG; video MP4/MOV).
const IMAGE_EXTENSION_MIME: Record<string, string> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
  ".heic": "image/heic",
  ".heif": "image/heif",
}

const VIDEO_EXTENSION_MIME: Record<string, string> = {
  ".mp4": "video/mp4",
  ".mov": "video/quicktime",
}

const AUDIO_EXTENSION_MIME: Record<string, string> = {
  ".mp3": "audio/mpeg",
  ".wav": "audio/wav",
  ".m4a": "audio/mp4",
}

const BLOCKED_MIMES = new Set(["image/bmp", "image/x-ms-bmp"])
const BLOCKED_EXTENSIONS = new Set([".bmp"])

const ALLOWED_IMAGE_MIMES = new Set(["image/png", "image/jpeg", "image/webp", "image/heic", "image/heif"])

const ALLOWED_VIDEO_MIMES = new Set(["video/mp4", "video/quicktime"])

const ALLOWED_AUDIO_MIMES = new Set([
  "audio/mpeg",
  "audio/mp3",
  "audio/wav",
  "audio/x-wav",
  "audio/wave",
  "audio/mp4",
  "audio/x-m4a",
  "audio/m4a",
])

export const REFERENCE_IMAGE_FILE_ACCEPT =
  "image/png,image/jpeg,image/webp,image/heic,image/heif,.png,.jpg,.jpeg,.webp,.heic,.heif"

export const REFERENCE_AUDIO_FILE_ACCEPT = "audio/mpeg,audio/wav,audio/mp4,audio/x-m4a,.mp3,.wav,.m4a"

export const REFERENCE_VIDEO_FILE_ACCEPT = "video/mp4,video/quicktime,.mp4,.mov"

export const REFERENCE_MEDIA_FILE_ACCEPT =
  `${REFERENCE_IMAGE_FILE_ACCEPT},${REFERENCE_VIDEO_FILE_ACCEPT},` + REFERENCE_AUDIO_FILE_ACCEPT

/** Seedance 2.0-shaped default window. Prefer maxVideoRefSeconds(model) for the current model. */
export const REFERENCE_VIDEO_MIN_SECONDS = 2
export const REFERENCE_VIDEO_MAX_SECONDS = 15

/** Потолок размера референса — тот же, что на сервере (It2tv::Media::ReferenceUpload). */
export const REFERENCE_MAX_UPLOAD_MB = 50
export const REFERENCE_MAX_UPLOAD_BYTES = REFERENCE_MAX_UPLOAD_MB * 1024 * 1024
export const REFERENCE_VIDEO_MAX_UPLOAD_MB = 200
export const REFERENCE_VIDEO_MAX_UPLOAD_BYTES = REFERENCE_VIDEO_MAX_UPLOAD_MB * 1024 * 1024

export function referenceMaxUploadMb(file: File): number {
  return isAllowedReferenceVideoFile(file) ? REFERENCE_VIDEO_MAX_UPLOAD_MB : REFERENCE_MAX_UPLOAD_MB
}

export function isReferenceFileWithinSize(file: File): boolean {
  const max = isAllowedReferenceVideoFile(file) ? REFERENCE_VIDEO_MAX_UPLOAD_BYTES : REFERENCE_MAX_UPLOAD_BYTES
  return file.size <= max
}

export const REFERENCE_IMAGE_VIDEO_FILE_ACCEPT = `${REFERENCE_IMAGE_FILE_ACCEPT},${REFERENCE_VIDEO_FILE_ACCEPT}`

export type ReferenceFileAcceptMode = "image" | "media" | "audio" | "image_video"

function fileExtension(name: string): string {
  const dot = name.lastIndexOf(".")
  if (dot < 0) return ""
  return name.slice(dot).toLowerCase()
}

export function inferReferenceFileMime(file: File): string {
  const type = file.type?.toLowerCase() ?? ""
  if (type) return type
  const ext = fileExtension(file.name)
  return IMAGE_EXTENSION_MIME[ext] ?? VIDEO_EXTENSION_MIME[ext] ?? AUDIO_EXTENSION_MIME[ext] ?? ""
}

export function isBlockedReferenceFile(file: File): boolean {
  const ext = fileExtension(file.name)
  if (BLOCKED_EXTENSIONS.has(ext)) return true
  const mime = inferReferenceFileMime(file)
  return BLOCKED_MIMES.has(mime)
}

export function isAllowedReferenceImageFile(file: File): boolean {
  if (isBlockedReferenceFile(file)) return false
  const mime = inferReferenceFileMime(file)
  if (!mime) return false
  if (ALLOWED_IMAGE_MIMES.has(mime)) return true
  const ext = fileExtension(file.name)
  return ext in IMAGE_EXTENSION_MIME
}

export function isAllowedReferenceAudioFile(file: File): boolean {
  if (isBlockedReferenceFile(file)) return false
  const mime = inferReferenceFileMime(file)
  if (!mime) return false
  if (ALLOWED_AUDIO_MIMES.has(mime)) return true
  const ext = fileExtension(file.name)
  return ext in AUDIO_EXTENSION_MIME
}

export function isAllowedReferenceVideoFile(file: File): boolean {
  if (isBlockedReferenceFile(file)) return false
  const mime = inferReferenceFileMime(file)
  if (!mime) return false
  if (ALLOWED_VIDEO_MIMES.has(mime)) return true
  const ext = fileExtension(file.name)
  return ext in VIDEO_EXTENSION_MIME
}

export function isAllowedReferenceMediaFile(file: File): boolean {
  if (isAllowedReferenceImageFile(file)) return true
  if (isAllowedReferenceAudioFile(file)) return true
  return isAllowedReferenceVideoFile(file)
}

export function filterReferenceImageFiles(files: File[]): File[] {
  return files.filter(isAllowedReferenceImageFile)
}

export function filterReferenceMediaFiles(files: File[]): File[] {
  return files.filter(isAllowedReferenceMediaFile)
}

export function filterReferenceAudioFiles(files: File[]): File[] {
  return files.filter(isAllowedReferenceAudioFile)
}

export function filterReferenceImageAndVideoFiles(files: File[]): File[] {
  return files.filter((file) => isAllowedReferenceImageFile(file) || isAllowedReferenceVideoFile(file))
}

export function filterReferenceFilesByMode(files: File[], mode: ReferenceFileAcceptMode): File[] {
  if (mode === "audio") return filterReferenceAudioFiles(files)
  if (mode === "image_video") return filterReferenceImageAndVideoFiles(files)
  return mode === "media" ? filterReferenceMediaFiles(files) : filterReferenceImageFiles(files)
}

export function readVideoDurationSeconds(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const video = document.createElement("video")
    video.preload = "metadata"
    const cleanup = () => {
      URL.revokeObjectURL(url)
      video.removeAttribute("src")
      video.load()
    }
    video.onloadedmetadata = () => {
      const duration = video.duration
      cleanup()
      if (!Number.isFinite(duration) || duration <= 0) {
        reject(new Error("unreadable video duration"))
        return
      }
      resolve(duration)
    }
    video.onerror = () => {
      cleanup()
      reject(new Error("unreadable video duration"))
    }
    video.src = url
  })
}

export function readAudioDurationSeconds(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const audio = document.createElement("audio")
    audio.preload = "metadata"
    const cleanup = () => {
      URL.revokeObjectURL(url)
      audio.removeAttribute("src")
      audio.load()
    }
    audio.onloadedmetadata = () => {
      const duration = audio.duration
      cleanup()
      if (!Number.isFinite(duration) || duration <= 0) {
        reject(new Error("unreadable audio duration"))
        return
      }
      resolve(duration)
    }
    audio.onerror = () => {
      cleanup()
      reject(new Error("unreadable audio duration"))
    }
    audio.src = url
  })
}

export async function isReferenceVideoDurationAllowed(
  file: File,
  maxSeconds = REFERENCE_VIDEO_MAX_SECONDS,
): Promise<boolean> {
  if (!isAllowedReferenceVideoFile(file)) return false
  try {
    const duration = await readVideoDurationSeconds(file)
    return duration >= REFERENCE_VIDEO_MIN_SECONDS && duration <= maxSeconds
  } catch {
    return false
  }
}
